import clsx from 'clsx'
import { mergeClassNames } from 'src/utils/tw'

export const StickyTh = ({
  children,
  isFirstColumn = false,
  isCentered = false,
  className,
}: {
  children: React.ReactNode
  isFirstColumn?: boolean
  isCentered?: boolean
  className?: string
}) => (
  <th
    scope='col'
    className={clsx(
      'text-dark sticky top-0 z-20 border-b border-slate-200 bg-slate-50 py-3.5 text-sm font-semibold',
      !isCentered && 'text-left',
      isCentered && 'text-center',
      isFirstColumn && 'pl-6 pr-3',
      !isFirstColumn && 'px-3',
      className,
    )}
  >
    {children}
  </th>
)

export const Th = ({
  children,
  isFirstColumn = false,
  isCentered = false,
  className,
}: {
  children: React.ReactNode
  isFirstColumn?: boolean
  isCentered?: boolean
  className?: string
}) => (
  <th
    scope='col'
    className={clsx(
      'text-dark border-b border-slate-200 bg-slate-50 py-3.5 text-left text-sm font-semibold',
      !isCentered && 'text-left',
      isCentered && 'text-center',
      isFirstColumn && 'pl-6 pr-3',
      !isFirstColumn && 'px-3',
      className,
    )}
  >
    {children}
  </th>
)

const Td = ({
  children,
  className,
  isFirstColumn = false,
}: {
  children: React.ReactNode
  className?: string
  isFirstColumn?: boolean
}) => (
  <td
    className={clsx(
      'whitespace-nowrap border-b border-slate-200 py-4 text-sm',
      !isFirstColumn && 'text-light px-3',
      isFirstColumn && 'text-dark pl-6 pr-3 font-medium',
      className,
    )}
  >
    {children}
  </td>
)

const RoundedTable = ({
  children,
  className,
  compact = false,
  tableClassName,
  ...rest
}: {
  children: React.ReactNode
  compact?: boolean
  className?: string
  tableClassName?: string
}) => (
  <div
    className={mergeClassNames(
      'flex h-full flex-col overflow-hidden rounded-lg bg-white shadow ring-1 ring-slate-200',
      className,
    )}
  >
    <div className='flex-1 overflow-auto'>
      <table
        className={mergeClassNames('w-full table-auto border-separate border-spacing-0', tableClassName)}
        {...rest}
      >
        {children}
      </table>
    </div>
  </div>
)

const Thead = ({ children }: { children: React.ReactNode }) => (
  <thead className='bg-uibg-1 align-[inherit]'>{children}</thead>
)

const Tbody = ({ children }: { children: React.ReactNode }) => (
  <tbody className='divide-y divide-slate-200 bg-white align-[inherit]'>{children}</tbody>
)

interface BasicTableProps {
  columnHeaders: React.ReactNode[]
  rows: React.ReactNode[][]
  className?: string
  compact?: boolean
  tableClassName?: string
}

export const BasicTable = ({
  columnHeaders,
  rows,
  compact = false,
  className,
  tableClassName,
  ...rest
}: BasicTableProps) => (
  <RoundedTable compact={compact} className={className} tableClassName={tableClassName} {...rest}>
    <Thead>
      <tr>
        {columnHeaders.map((header, index) => (
          <StickyTh key={`${header?.toString()}${index}`} isFirstColumn={index === 0} className={compact ? '' : ''}>
            {header}
          </StickyTh>
        ))}
      </tr>
    </Thead>
    <Tbody>
      {rows.map((row, i) => (
        <tr key={i}>
          {row.map((cell, index) => (
            <Td key={index} isFirstColumn={index === 0} className={compact ? '!py-1' : ''}>
              {cell}
            </Td>
          ))}
        </tr>
      ))}
    </Tbody>
  </RoundedTable>
)
